import React, { useEffect, useState, createRef } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import { FileDropZone, excel } from 'components/layout/mui-dropzone';
import {
  setImportRows, validateGeoOnServer, autoFillDescriptives, setImportGeoValidationResult,
  submitImport, setImportName, setConfirmationModalState, setImportRowsSkippedCount,
  setImportRawData, appNotify, setImportNameValidation, setImportDupCheckErrors, setImportLoadingOverlayState, revalidateImportRows
} from 'store/actions';
import { importTypeStateSelector, canImportSelector } from 'store/selectors';
import {
  FormControlLabel,
  Button,
  Tooltip,
  Divider,
  Typography,
  TextField,
  Paper,
  Switch
} from '@material-ui/core/';
import { ButtonDropdown } from 'components/layout';
import { LocationVerificationModal } from 'components/underwrite/acquisition/import/importOpportunities/LocationVerification';
import { ImportPropertiesGrid } from 'components/explore/import/importProperties';
import { ImportExpander, InlineBadge } from 'components/underwrite/shared/import';
import xlsxSampleSheet from 'static/assets/ReVUe_SampleImport.xlsx';
import { sendClientLog } from 'services/logService';
import debounce from 'lodash/debounce';
import { Constants } from 'utilities/constants';
import { format } from 'date-fns';
import dataGridExtension from 'components/devExtreme/dataGridExtension';

const gridRef = createRef();

const ImportPropertiesControls = ({
  classes,
  history,
  canImport,
  importGeoValidationResult,
  importRows,
  importName,
  importNameValidation,
  gridEditDirty,
  geoValidationResult,
  dupCheckErrors,
  importRowsSkippedCount,
  setImportRows,
  setImportRowsSkippedCount,
  revalidateImportRows,
  validateGeoOnServer,
  autoFillDescriptives,
  setImportGeoValidationResult,
  setImportDupCheckErrors,
  setConfirmationModalState,
  submitImport,
  setImportName,
  setImportNameValidation,
  redirectToImportStatusTab,
  setImportRawData,
  setImportLoadingOverlayState,
  appNotify
}) => {
  // const [containsCounty, setContainsCounty] = useState(false); // This is to check if the address object returned from google autocomplete contains a county/equivalent. (ex: Don't allow users submit if they type a state)
  const [openLocationVerification, setOpenLocationVerification] = useState(false);
  const [addressToVerify, setAddressToVerify] = useState(null);
  const [contextRowKey, setContextRowKey] = useState(null);
  const [filterByErrors, setFilterByErrors] = useState(false);
  const invalidZipCodes = geoValidationResult.invalidZipCodes || [];
  const invalidCounties = geoValidationResult.invalidCounties || [];
  const invalidGeo = geoValidationResult.invalidGeo || [];
  const dupRowReferenceIds = dupCheckErrors?.map(error => error.referenceId) || [];
  const hasErrors = importRows.some(row => row.invalidColumns?.length > 0) || invalidZipCodes.length || invalidCounties.length || invalidGeo.length || dupRowReferenceIds.length;
  const maxCountExceeded = importRows?.length > Constants.ImportMaxCount.PROPERTIES;

  useEffect(() => {
    sendClientLog('Info', 'Import - User loaded Properties import tab');
  }, []);

  useEffect(() => {
    const dataSource = gridRef.current.instance.getDataSource();
    if (!(dataSource && importRows)) return;

    if (filterByErrors) {
      if (!hasErrors) { // If no more errors (or if no importRows), automatically set filter toggle off and remove from grid
        setFilterByErrors(false);
        gridRef.current.instance.clearFilter();
      } else { // Else, apply filter to grid data
        gridRef.current.instance.filter(row => {
          return row.invalidColumns?.length > 0 || invalidZipCodes.includes(row.zipCode) || invalidCounties.includes(row.referenceId) || invalidGeo.includes(row.referenceId) || dupRowReferenceIds.includes(row.referenceId);
        });
      }
    } else if (gridRef.current.instance.filter()) { // If filter toggle is off, clear filter from grid
      gridRef.current.instance.clearFilter();
    }
  }, [importRows, filterByErrors])

  const onDropZoneFilesAdded = async (addedFiles) => {
    let combinedData = [];
    try {
      setImportLoadingOverlayState({ // Start displaying loading here since sheets with high row count can take some time. Loading will be turned off in importRawDataSaga after validation is finished.
        isLoading: true,
        message: `Loading file...`
      });
      for (const file of addedFiles) {
        if (excel.excelMimeTypes.indexOf(file.type.toLowerCase()) >= 0) {
          const spreadsheet = await excel.parseExcel(file);
          // Only importing first sheet, should import all?
          console.log(spreadsheet)
          const DragAndDropData = spreadsheet[Object.keys(spreadsheet)[0]];
          combinedData = combinedData.concat(DragAndDropData);
          //console.log(spreadsheet);
          // setImportRawData(spreadsheet);
        } else {
          // const text = await files.loadFileAsText(file);
          //console.log(text);
        }
      }
      if (filterByErrors) {
        setFilterByErrors(false);
      }
      setImportRawData(combinedData, 'file');
    } catch {
      setImportLoadingOverlayState({
        isLoading: false,
        message: null
      });
    }
  }
  const onDropZoneFilesRejected = files => {
    //console.log('Files rejected:', files);
  }

  const handlePaste = async e => {
    // First, ask the Permissions API if we have some kind of access to
    // the "clipboard-read" feature.
    // await navigator.permissions.revoke({name:'clipboard-read'}).then(function(result) {
    //   console.log(result.state);
    // });
    const result = await navigator.permissions.query({ name: 'clipboard-read' });
    console.log(navigator.permissions)
    // If permission to read the clipboard is granted or if the user will
    // be prompted to allow it, we proceed.
    console.log(result.state)
    // if (result.state === 'granted' || result.state === 'prompt') {
    try {
      const data = await navigator.clipboard.readText();
      // console.log(data);
      if (filterByErrors) {
        setFilterByErrors(false);
      }
      setImportRawData(data, 'paste');
    } catch (err) {
      // alert('Paste Error: ' + JSON.stringify(err));
      // Snackbar instead of alert message, unsure how to reproduce this error. Maybe if clipboard permission is not allowed?
      //TODO: perhaps generate a nicer message to the user?
      appNotify(`Paste Error: ${JSON.stringify(err)}`, 'error');
    }
    // } else {
    //   console.log(result.state)
    //   appNotify(`Unable to read from clipboard`, 'error');
    // }
  };

  // Clear click, hide validation error message and set import rows to []
  const handleClearClick = () => {
    resetGeoValidationResult();
    resetDupCheckResult();
    setImportRows([]); // clear out rows
    setImportRowsSkippedCount(0);
  };

  const handleExport = () => {
    const timestamp = format(new Date(), '_yyyy-MM-dd_HHmmss');
    const fileName = 'ReVUe_PropertyImport' + timestamp;
    const sheetName = 'Sheet';
    const customizeCell = options => {
      const { excelCell, gridCell } = options;
      const { data, column } = gridCell;
      if (!data?.invalidColumns?.length) return;
      if (data.invalidColumns.some(col => col.name === column.dataField)) { // Check if current cell's column is contained in invalidColumns
        excelCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '00ffc9c9' }
        }
      }
    }
    dataGridExtension.exportToExcel(gridRef, fileName, sheetName, customizeCell);
    sendClientLog('Info', 'Import - User exported Properties grid to Excel');
  }

  const onSubmitImport = () => {
    if (importRows.length === 0) {
      appNotify('Please Add Data', 'warning');
      return;
    }
    if (!canImport) {
      if (importGeoValidationResult.invalidGeo.length > 0) {
        appNotify('Please verify Location', 'warning');
      } else {
        appNotify('Please fix invalid input', 'warning');
      }
      return;
    }
    setConfirmationModalState({ // Open confirmation modal
      isModalOpen: true,
      handleConfirmation: handleSubmitImportConfirmation,
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to import ${importRows.length} properties?`
    });
  };

  const handleSubmitImportConfirmation = () => {
    closeConfirmationModal();
    submitImport(onSubmitImportSuccess);
    sendClientLog('Info', `Import - User clicked Import for ${importRows.length} properties.`);
  }
  const onSubmitImportSuccess = () => {
    resetGeoValidationResult();
    resetDupCheckResult();
    redirectToImportStatusTab(history);
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  }

  const onValidateGeoClick = () => {
    if (importRows.length === 0) { // Check if there even is data to be imported
      appNotify('Please Add Data', 'warning');
      return;
    }
    if (!canImport) {
      appNotify('Please fix invalid input', 'warning');
      return;
    }
    resetGeoValidationResult();
    validateGeoOnServer();
  }

  const onAutoFillClick = (e) => {
    if (importRows.length === 0) { // Check if there even is data to be imported
      appNotify('Please Add Data', 'warning');
      return;
    }
    if (importName.length < 3) {
      setImportNameValidation({ valid: false, message: "Import name must have a minimum of 3 characters." });
    }
    sendClientLog('Info', `Import - User clicked autofill.`);
    resetGeoValidationResult();// Need to rest server validation result otherwise when auto fill corrects error UI will still show that row/cell is invalid
    autoFillDescriptives(e.value);
  }

  const resetGeoValidationResult = () => {
    setImportGeoValidationResult({ passedValidation: null, invalidZipCodes: null, invalidCounties: null, invalidGeo: null });
  }

  const resetDupCheckResult = () => {
    setImportDupCheckErrors([]);
  }

  const debouncedImportNameRequest = debounce(value => setImportName(value), 1000);

  const onImportNameChange = e => {
    debouncedImportNameRequest(e?.target?.value ?? "");
  }

  const getErrorCells = () => {
    let errorArray = [];
    if (importRows.length) {
      importRows.forEach(row => {
        if (row.invalidColumns?.length > 0) {
          row.invalidColumns.forEach(column => {
            const err = errorArray.find(el => el.message === column.message);
            if (err) {
              err.count += 1;
            } else {
              errorArray.push({
                message: column.message,
                count: 1
              })
            }
          });
        }
      });
    }

    // Send all notifications in an array
    return errorArray;
  }

  const handleLocationVerificationClose = e => {
    setOpenLocationVerification(false);
  }

  const updateLocation = geoResultDto => {
    const { invalidZipCodes, invalidCounties, invalidGeo, geoValidationDtos } = geoValidationResult;
    const newImportRows = importRows.map(row => { // Update grid with new location
      if (row.referenceId === contextRowKey) {
        return {
          ...row,
          latitude: geoResultDto.propertyMasterResult.latitude,
          longitude: geoResultDto.propertyMasterResult.longitude,
          street: geoResultDto.propertyMasterResult.cleanAddress,
          city: geoResultDto.propertyMasterResult.city,
          county: geoResultDto.propertyMasterResult.county,
          state: geoResultDto.propertyMasterResult.state,
          zipCode: geoResultDto.propertyMasterResult.zip,
          locationVerificationUsed: true
        }
      }
      return row;
    })
    // If missing lat/long was the only reason for failed validation, and the user has placed a pin in LocationVerification to provide lat/long, mark property as passed
    const overridePassedValidation = geoResultDto.onlyMissingLatLong && geoResultDto.propertyMasterResult.latitude && geoResultDto.propertyMasterResult.latitude
      ? true
      : geoResultDto.passedValidation
    const newGeoValidationDtos = geoValidationDtos.map(dto => { // Update geo validation results with new location
      if (dto.referenceId === contextRowKey) {
        return {
          ...dto,
          street: geoResultDto.propertyMasterResult.cleanAddress,
          zipCode: geoResultDto.propertyMasterResult.zip,
          geoResultDto: {
            ...geoResultDto,
            passedValidation: overridePassedValidation
          }
        }
      }
      return dto;
    })
    const newInvalidGeo = invalidGeo && (geoResultDto.locationCreated || overridePassedValidation) ? invalidGeo.filter(id => id !== contextRowKey) : invalidGeo;
    const newPassedValidation = !invalidZipCodes.length && !invalidCounties.length && !newInvalidGeo?.length;
    setImportGeoValidationResult({
      passedValidation: newPassedValidation,
      invalidZipCodes,
      invalidCounties,
      invalidGeo: newInvalidGeo,
      geoValidationDtos: newGeoValidationDtos
    });
    setImportRows(newImportRows);
    //handleLocationVerificationClose();
    // setAddressToVerify(null);
  }

  const onRemoveAllClick = errorType => {
    setConfirmationModalState({ // Open confirmation modal
      isModalOpen: true,
      handleConfirmation: () => handleRemoveAllErrorRows(errorType),
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to remove all rows with errors?`
    });
  }

  const handleRemoveAllErrorRows = errorType => {
    closeConfirmationModal();
    let filteredRows;
    let deletedKeys = [];
    switch (errorType) {
      case 'format':
        filteredRows = importRows.filter(row => !row.invalidColumns?.length);
        revalidateImportRows(filteredRows);
        break;
      case 'geo':
        filteredRows = importRows.filter(row => {
          const rowHasError = invalidZipCodes.includes(row.zipCode) || invalidCounties.includes(row.referenceId) || invalidGeo.includes(row.referenceId);
          if (rowHasError) {
            deletedKeys.push(row.referenceId);
          }
          return !rowHasError;
        });
        if (!deletedKeys.length) break;
        setImportRows(filteredRows);
        setImportGeoValidationResult({
          passedValidation: true,
          invalidZipCodes: [],
          invalidCounties: [],
          invalidGeo: [],
          geoValidationDtos: geoValidationResult.geoValidationDtos.filter(dto => !deletedKeys.includes(dto.referenceId))
        });
        break;
      case 'dupCheck':
        filteredRows = importRows.filter(row => {
          const rowHasError = dupRowReferenceIds.includes(row.referenceId);
          if (rowHasError) {
            deletedKeys.push(row.referenceId);
          }
          return !rowHasError;
        });
        if (!deletedKeys.length) break;
        setImportRows(filteredRows);
        setImportDupCheckErrors(dupCheckErrors.filter(oldError => !deletedKeys.includes(oldError.referenceId)));
        break;
      default: return;
    }
  }

  const errorCells = getErrorCells();
  const hasGeoErrors = (invalidZipCodes?.length) || (invalidCounties?.length) || (invalidGeo?.length);
  return (
    <>
      <div className={classes.container}>
        <div className={classes.leftControls}>
          <TextField error={importNameValidation.valid === false} required label="Import Name" onChange={onImportNameChange} defaultValue={importName} helperText={importNameValidation.message} />
          <Divider className={classes.verticalDivider} />
          <ButtonDropdown
            title='Autofill'
            // tooltip='Autofill missing indicatives'
            onSelect={onAutoFillClick}
            items={[{ text: 'Blanks Only', value: 'blanks' }, { text: 'All Fields', value: 'all' }]}
            style={{ margin: '0 4px 0 4px' }}
            buttonWidth={119}
            menuWidth={119}
            disabled={gridEditDirty || maxCountExceeded}
          />
          <Divider className={classes.verticalDivider} />
          <Tooltip title="Validate zipcode, county and geo location">
            <div>
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: '4px', marginRight: '4px' }}
                onClick={onValidateGeoClick}
                disabled={!importRows.length || importGeoValidationResult.passedValidation || gridEditDirty || maxCountExceeded}
              >
                Validate Geo
            </Button>
            </div>
          </Tooltip>
          <Divider className={classes.verticalDivider} />
          <Button
            variant="contained"
            color="primary"
            style={{ marginLeft: '4px', marginRight: '4px' }}
            onClick={onSubmitImport}
            disabled={!importGeoValidationResult.passedValidation || !canImport || gridEditDirty || maxCountExceeded || importName.length === 0 || (importNameValidation.valid == null || importNameValidation.valid === false)}
          >
            Import
          </Button>
        </div>
        <div className={classes.centerControls}>
          <Typography variant='h5' noWrap>Property Import</Typography>
        </div>
        <div className={classes.rightControls} >
          <Button variant='outlined' color='primary' onClick={handlePaste}>
            Paste from Clipboard
          </Button>
          <Button
            variant="outlined"
            color="primary"
            style={{ marginLeft: '4px', marginRight: '4px' }}
            onClick={handleClearClick}
            disabled={!importRows.length}
          >
            Clear
          </Button>
          <Tooltip title="Export with validation errors">
            <div>
              <Button
                variant="contained"
                color="primary"
                // style={{ marginLeft: '4px', marginRight: '4px' }}
                onClick={handleExport}
                // disabled={!importRows.length || canImport || filterByErrors}
                disabled={!importRows.length || gridEditDirty}
              >
                Export
              </Button>
            </div>
          </Tooltip>
          <Divider className={classes.verticalDivider} />
          <a href={xlsxSampleSheet} download="ReVUe_SampleImport.xlsx" className={classes.xlsxSampleButton}>
            <Tooltip title="Download import template">
              <Button variant="outlined" color="primary">
                Sample Sheet
              </Button>
            </Tooltip>
          </a>
        </div>
      </div>
      <FileDropZone
        width='100%'
        acceptedMimeTypes={[...excel.excelMimeTypes, 'text/plain']}
        onFilesAdded={onDropZoneFilesAdded}
        onFilesRejected={onDropZoneFilesRejected}
        elevation={2}
        dragOverElevation={10}
        className='gridFileDropZone'
        style={{ cursor: 'pointer', minHeight: '70px' }}
      />
      {(errorCells.length || importRowsSkippedCount) && !maxCountExceeded
        ? <ImportExpander
          title='Cell errors found'
        >
          <div>
            <FormControlLabel
              control={<Switch checked={filterByErrors} onChange={() => setFilterByErrors(!filterByErrors)} />}
              label="Filter by rows with errors"
              disabled={!hasErrors}
            />
            <Tooltip title='Remove all rows with errors'>
              <span>
                <Button
                    variant="outlined"
                    className={classes.redButton}
                    size='small'
                    style={{ marginLeft: '4px', marginRight: '4px' }}
                    onClick={() => onRemoveAllClick('format')}
                    disabled={!errorCells.length || gridEditDirty || maxCountExceeded}
                >
                  Remove All
                </Button>
              </span>
            </Tooltip>
          </div>
          {errorCells.map(error => {
            return (
              <div key={error.message} className={classes.errorTypeContainer}>
                <InlineBadge className={classes.errorCountBadge} badgeContent={error.count} error color='primary' />
                <Typography>{error.message}</Typography>
              </div>
            )
          })}
          {importRowsSkippedCount > 0
            ? <div className={classes.errorTypeContainer}>
              <InlineBadge className={classes.errorCountBadge} badgeContent={importRowsSkippedCount} color='primary' />
              <Typography>Row skipped - no Source ID found</Typography>
            </div>
            : null
          }
        </ImportExpander>
        : null
      }
      {hasGeoErrors
        ? <ImportExpander
          title='Geo validation errors found'
        >
          <div>
            <FormControlLabel
              control={<Switch checked={filterByErrors} onChange={() => setFilterByErrors(!filterByErrors)} />}
              label="Filter by rows with errors"
              disabled={!hasErrors}
            />
            <Tooltip title='Remove all rows with geo validation errors'>
              <span>
                <Button
                    variant="outlined"
                    className={classes.redButton}
                    size='small'
                    style={{ marginLeft: '4px', marginRight: '4px' }}
                    onClick={() => onRemoveAllClick('geo')}
                    disabled={gridEditDirty || maxCountExceeded}
                >
                  Remove All
                </Button>
              </span>
            </Tooltip>
          </div>
          {invalidZipCodes.length
            ? <div className={classes.errorTypeContainer}>
              <InlineBadge className={classes.errorCountBadge} badgeContent={invalidZipCodes.length} error color='primary' />
              <Typography>Invalid zip code found</Typography>
            </div>
            : null
          }
          {invalidCounties.length
            ? <div className={classes.errorTypeContainer}>
              <InlineBadge className={classes.errorCountBadge} badgeContent={invalidCounties.length} error color='primary' />
              <Typography>Invalid county found</Typography>
            </div>
            : null
          }
          {invalidGeo.length
            ? <div className={classes.errorTypeContainer}>
              <InlineBadge className={classes.errorCountBadge} badgeContent={invalidGeo.length} error color='primary' />
              <Typography>Property location needs verification</Typography>
            </div>
            : null
          }
        </ImportExpander>
        : null
      }
      {dupCheckErrors?.length
        ? <ImportExpander
          title='Import errors found'
        >
          <div>
            <FormControlLabel
              control={<Switch checked={filterByErrors} onChange={() => setFilterByErrors(!filterByErrors)} />}
              label="Filter by rows with errors"
              disabled={!hasErrors}
            />
            <Tooltip title='Remove all rows with errors'>
              <span>
                <Button
                    variant="outlined"
                    className={classes.redButton}
                    size='small'
                    style={{ marginLeft: '4px', marginRight: '4px' }}
                    onClick={() => onRemoveAllClick('dupCheck')}
                    disabled={gridEditDirty || maxCountExceeded}
                >
                  Remove All
                </Button>
              </span>
            </Tooltip>
          </div>
          {dupCheckErrors.map(error => <div className={classes.errorTypeContainer}><Typography>{error.errorMessage}</Typography></div>)}
        </ImportExpander>
        : null
      }
      <Paper style={{ flexGrow: 1, overflow: 'hidden' }} className={classes.gridPaper}>
        <ImportPropertiesGrid
          ref={gridRef}
          setAddressToVerify={setAddressToVerify}
          setOpenLocationVerification={setOpenLocationVerification}
          setContextRowKey={setContextRowKey}
        />
        {gridRef.current && openLocationVerification ?
          <LocationVerificationModal open={openLocationVerification} onClose={handleLocationVerificationClose} geoValidationDto={addressToVerify} updateLocation={updateLocation} jitCreationType='properties' /> : null
        }
      </Paper>
    </>
  );
};

const styles = theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1)
  },
  leftControls: {
    display: 'flex',
    width: '40%',
    minWidth: 'fit-content',
    alignItems: 'center',
    minHeight: 68
  },
  rightControls: {
    display: 'flex',
    width: '40%',
    justifyContent: 'flex-end',
    marginBottom: 5
  },
  centerControls: {
    display: 'flex',
  },
  alignment: {
    margin: 0,
    verticalAlign: 'middle'
  },
  xlsxSampleButton: {
    textDecoration: 'none' // Remove text decoration underline from anchor tag to make it look like a button
  },
  verticalDivider: {
    width: 1,
    height: 36,
    margin: '0px 8px',
  },
  gridPaper: {
    padding: '5px 8px 8px'
  },
  errorTypeContainer: {
    display: 'flex',
    marginBottom: 1
  },
  errorCountBadge: {
    marginRight: 8
  },
  redButton: {
    color: theme.palette.red.main,
    border: `1px solid ${theme.palette.red.main}80`,
    '&:hover': {
      border: `1px solid ${theme.palette.red.main}`,
      backgroundColor: `${theme.palette.red.main}0a`
    }
  }
});

const mapStateToProps = state => {
  const propertiesState = importTypeStateSelector(state, Constants.ImportType.PROPERTIES);
  const canImport = canImportSelector(state, Constants.ImportType.PROPERTIES);
  return {
    canImport, // this only means that import passed CLIENT validation
    importGeoValidationResult: propertiesState.importGeoValidationResult,
    importRows: propertiesState.importRows,
    importName: propertiesState.importName,
    importNameValidation: propertiesState.importNameValidation,
    gridEditDirty: propertiesState.gridEditDirty,
    geoValidationResult: propertiesState.importGeoValidationResult,
    dupCheckErrors: propertiesState.importDupCheckErrors,
    importRowsSkippedCount: propertiesState.importRowsSkippedCount
  }
};
const mapDispatchToProps = dispatch => {
  const importType = Constants.ImportType.PROPERTIES;

  return {
    setImportRows: importRows => dispatch(setImportRows({ importType, importRows })),
    setImportRowsSkippedCount: importRowsSkippedCount => dispatch(setImportRowsSkippedCount({ importType, importRowsSkippedCount })),
    revalidateImportRows: (rows) => dispatch(revalidateImportRows({ importType, rows })),
    validateGeoOnServer: () => dispatch(validateGeoOnServer({ importType })),
    autoFillDescriptives: fieldType => dispatch(autoFillDescriptives({ importType, fieldType })),
    setImportGeoValidationResult: (importGeoValidationResult) => dispatch(setImportGeoValidationResult({ importType, importGeoValidationResult })),
    setImportDupCheckErrors: (dupCheckErrors) => dispatch(setImportDupCheckErrors({ importType, dupCheckErrors })),
    setConfirmationModalState: confirmationModalState => dispatch(setConfirmationModalState(confirmationModalState)),
    submitImport: onSubmitImportSuccess => dispatch(submitImport({ importType, onSubmitImportSuccess })),
    setImportName: importName => dispatch(setImportName({ importType, importName })),
    setImportNameValidation: importNameValidation => dispatch(setImportNameValidation({ importType, importNameValidation })),
    redirectToImportStatusTab: history => {
      history.push(Constants.Routes.EXPLORE_IMPORT_STATUS);
    },
    setImportRawData: (data, source) => dispatch(setImportRawData({ data, importType, source })),
    setImportLoadingOverlayState: (importLoadingOverlayState) => dispatch(setImportLoadingOverlayState({ importType, importLoadingOverlayState })),
    appNotify: (message, variant, options) => dispatch(appNotify(message, variant, options))
  }
};
export default compose(
  withRouter,
  withStyles(styles),
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ImportPropertiesControls);